/**
 * @fileoverview Router Index
 * @description Main router configuration for the application
 * @module @xtream/frontend/router
 *
 * This file configures:
 * - All application routes with lazy loading
 * - Route protection (PrivateRoute, PublicRoute)
 * - Error boundaries for route errors
 * - Layout wrappers for different route groups
 *
 * OWNERSHIP: This file is owned by FASE 12 (Dev13)
 */

import React, { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { UserRole } from '@xtream/shared-types';
import { useAuthStore } from '@store/index';
import { ROUTES } from './routes';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';

// ═══════════════════════════════════════════════════════════════════════════
// LAZY-LOADED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

// Pages - All pages are now implemented, no catch fallbacks needed
const LoginPage = lazy(() => import('@pages/Login'));
const DashboardPage = lazy(() => import('@pages/Dashboard'));
const ServicesListPage = lazy(() => import('@pages/Services/ServicesList'));
const ServiceDetailPage = lazy(() => import('@pages/Services/ServiceDetail'));
const ServiceFormPage = lazy(() => import('@pages/Services/ServiceForm'));
const ProfilePage = lazy(() => import('@pages/Profile'));
const UsersListPage = lazy(() => import('@pages/Users'));
const UserFormPage = lazy(() => import('@pages/Users/UserFormPage'));
const SettingsPage = lazy(() => import('@pages/Settings'));
const NotFoundPage = lazy(() => import('@pages/NotFound'));

// Layouts
const MainLayout = lazy(() => import('@components/layout/MainLayout'));
const AuthLayout = lazy(() => import('@components/layout/AuthLayout'));

// ═══════════════════════════════════════════════════════════════════════════
// LOADING COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const LoadingFallback: React.FC = () => (
  <div className="loading-fallback">
    <div className="loading-fallback__spinner" />
    <p className="loading-fallback__text">Cargando...</p>
  </div>
);

// Note: PlaceholderPage removed - all pages are now implemented

// ═══════════════════════════════════════════════════════════════════════════
// LAYOUT WRAPPERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wrapper for main application layout
 * Connects the MainLayout to global state
 */
const MainLayoutWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <Suspense fallback={<LoadingFallback />}>
      <MainLayout
        user={user as Parameters<typeof MainLayout>[0]['user']}
        activePath={location.pathname}
        onNavigate={(href) => navigate(href)}
        onLogout={handleLogout}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    </Suspense>
  );
};

/**
 * Wrapper for auth pages
 * Connects AuthLayout to global state
 */
const AuthLayoutWrapper: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <AuthLayout>
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      </AuthLayout>
    </Suspense>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ROUTER CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to={ROUTES.DASHBOARD} replace />,
  },
  {
    element: <AuthLayoutWrapper />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: (
          <PublicRoute redirectAuthenticated>
            <LoginPage />
          </PublicRoute>
        ),
      },
    ],
  },
  {
    element: <MainLayoutWrapper />,
    children: [
      {
        path: ROUTES.DASHBOARD,
        element: (
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.SERVICES,
        element: (
          <PrivateRoute>
            <ServicesListPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.SERVICE_NEW,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.OPERATOR]}>
            <ServiceFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.SERVICE_DETAIL,
        element: (
          <PrivateRoute>
            <ServiceDetailPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.SERVICE_EDIT,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN, UserRole.OPERATOR]}>
            <ServiceFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <PrivateRoute>
            <ProfilePage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.USERS,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN]}>
            <UsersListPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.USER_NEW,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN]}>
            <UserFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.USER_EDIT,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN]}>
            <UserFormPage />
          </PrivateRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <PrivateRoute allowedRoles={[UserRole.ADMIN]}>
            <SettingsPage />
          </PrivateRoute>
        ),
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};

export { ROUTES, routeConfigs, getNavigationItems, buildPath } from './routes';
export { PrivateRoute } from './PrivateRoute';
export { PublicRoute } from './PublicRoute';

export default AppRouter;
